import axios from 'axios';

const API_URL = '/api/generation';

// Configure axios to send credentials with requests
axios.defaults.withCredentials = true;

const aiService = {
  /**
   * Start core values discovery conversation
   */
  async startCoreValues() {
    try {
      const response = await axios.post(`${API_URL}/core-values/start`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to start conversation'
      };
    }
  },

  /**
   * Respond in core values conversation
   */
  async respondCoreValues(userResponse, conversation) {
    try {
      const response = await axios.post(`${API_URL}/core-values/respond`, {
        response: userResponse,
        conversation
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send response'
      };
    }
  },

  /**
   * Extract core values from conversation
   */
  async extractCoreValues(conversation) {
    try {
      const response = await axios.post(`${API_URL}/core-values/extract`, {
        conversation
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to extract values'
      };
    }
  },

  /**
   * Get saved core values
   */
  async getCoreValues() {
    try {
      const response = await axios.get(`${API_URL}/core-values`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get core values'
      };
    }
  },

  /**
   * Update a core value
   */
  async updateCoreValue(valueId, updates) {
    try {
      const response = await axios.put(`${API_URL}/core-values/${valueId}`, updates);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update value'
      };
    }
  },

  /**
   * Delete a core value
   */
  async deleteCoreValue(valueId) {
    try {
      const response = await axios.delete(`${API_URL}/core-values/${valueId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete value'
      };
    }
  },

  /**
   * Get saved conversation for a form type
   */
  async getConversation(formType) {
    try {
      const response = await axios.get(`${API_URL}/conversation/${formType}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get conversation'
      };
    }
  },

  /**
   * Generate mission statement options
   */
  async generateMission(formData) {
    try {
      const response = await axios.post(`${API_URL}/mission/generate`, formData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate mission statements'
      };
    }
  },

  /**
   * Save mission statement
   */
  async saveMission(data) {
    try {
      const response = await axios.post(`${API_URL}/mission/save`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to save mission statement'
      };
    }
  },

  /**
   * Generate vision statement options
   */
  async generateVision(formData) {
    try {
      const response = await axios.post(`${API_URL}/vision/generate`, formData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate vision statements'
      };
    }
  },

  /**
   * Save vision statement
   */
  async saveVision(data) {
    try {
      const response = await axios.post(`${API_URL}/vision/save`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to save vision statement'
      };
    }
  },

  /**
   * Refine mission statement for target market
   */
  async refineMission() {
    try {
      const response = await axios.post(`${API_URL}/mission/refine`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to refine mission statement'
      };
    }
  },

  /**
   * Refine vision statement for target market
   */
  async refineVision() {
    try {
      const response = await axios.post(`${API_URL}/vision/refine`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to refine vision statement'
      };
    }
  },

  // ============================================
  // Irresistible Offer Interview Methods
  // ============================================

  /**
   * Start irresistible offer interview conversation
   */
  async startIrresistibleOfferInterview() {
    try {
      const response = await axios.post('/api/irresistible-offer/start');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to start interview'
      };
    }
  },

  /**
   * Respond in irresistible offer interview conversation
   */
  async respondIrresistibleOfferInterview(userResponse, conversation) {
    try {
      const response = await axios.post('/api/irresistible-offer/respond', {
        response: userResponse,
        conversation
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send response'
      };
    }
  },

  /**
   * Get saved interview conversation for resume
   */
  async getIrresistibleOfferConversation() {
    try {
      const response = await axios.get('/api/irresistible-offer/get-conversation');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get conversation'
      };
    }
  },

  /**
   * Generate irresistible offer from interview
   */
  async generateIrresistibleOfferFromInterview(conversation, guaranteeType, guaranteeConditions) {
    try {
      const response = await axios.post('/api/irresistible-offer/generate', {
        conversation,
        guarantee_type: guaranteeType,
        guarantee_conditions: guaranteeConditions
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate offer'
      };
    }
  }
};

export default aiService;
